// src/pages/AddEmployee.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  MenuItem,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AddEmployee = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    personal_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    nationality: 'LT',
    address_street: '',
    address_city: '',
    address_postal_code: '',
    address_country: 'LT',
    employment_type: 'temporary',
    sodra_code: '',
    tax_code: '',
    bank_account: '',
    bank_name: '',
    work_permit_number: '',
    work_permit_valid_until: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    try {
      await api.post('/employees/register', formData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/employees');
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to register employee');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Register New Employee
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Employee registered successfully! Redirecting...</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Personal Code (Asmens Kodas)"
                name="personal_code"
                value={formData.personal_code}
                onChange={handleChange}
                placeholder="39001010000"
                helperText="11-digit Lithuanian personal code"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+370 600 12345"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
              >
                <MenuItem value="LT">Lithuania</MenuItem>
                <MenuItem value="UA">Ukraine</MenuItem>
                <MenuItem value="BY">Belarus</MenuItem>
                <MenuItem value="PL">Poland</MenuItem>
                <MenuItem value="LV">Latvia</MenuItem>
                <MenuItem value="EE">Estonia</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Address
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street Address"
                name="address_street"
                value={formData.address_street}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="City"
                name="address_city"
                value={formData.address_city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Postal Code"
                name="address_postal_code"
                value={formData.address_postal_code}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Employment Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Employment Type"
                name="employment_type"
                value={formData.employment_type}
                onChange={handleChange}
              >
                <MenuItem value="temporary">Temporary</MenuItem>
                <MenuItem value="permanent">Permanent</MenuItem>
                <MenuItem value="seasonal">Seasonal</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="SODRA Code"
                name="sodra_code"
                value={formData.sodra_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tax Code"
                name="tax_code"
                value={formData.tax_code}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Banking Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Account (IBAN)"
                name="bank_account"
                value={formData.bank_account}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {formData.nationality !== 'LT' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Work Permit (For Non-EU Citizens)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Work Permit Number"
                    name="work_permit_number"
                    value={formData.work_permit_number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Work Permit Valid Until"
                    name="work_permit_valid_until"
                    type="date"
                    value={formData.work_permit_valid_until}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Emergency Contact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                name="emergency_contact_name"
                value={formData.emergency_contact_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                name="emergency_contact_phone"
                value={formData.emergency_contact_phone}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
            >
              Register Employee
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/admin/employees')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default AddEmployee;
